import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Upload, Search, Edit3, Activity, 
  Stethoscope, Package, Users, X, Save,
  Building2, ChevronLeft, ChevronRight,
  CheckCircle2, AlertCircle, RefreshCw
} from 'lucide-react';
import { hasRole } from '../utils/auth';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const MasterDataManagement = () => {
  const [activeTab, setActiveTab] = useState('medicine');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // --- STATE HIỆU CHỈNH ---
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  // --- STATE TOAST THÔNG BÁO ---
  const [toastMessage, setToastMessage] = useState(null);

  // --- STATE PHÂN TRANG ---
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50; 

  const showToast = (message, isError = false) => {
    setToastMessage({ text: message, isError });
    setTimeout(() => setToastMessage(null), 3500);
  };

  const fetchCategoryData = async (tab) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await axios.get(`${API_URL}/categories/${tab}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (err) {
      console.error("Lỗi tải danh mục:", err);
      showToast("Lỗi khi tải dữ liệu danh mục!", true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategoryData(activeTab);
  }, [activeTab]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, searchTerm]);

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', activeTab);

    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(`${API_URL}/categories/import`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}` 
        }
      });
      showToast("Nạp danh mục thành công!");
      fetchCategoryData(activeTab);
    } catch (err) {
      showToast("Lỗi import: " + (err.response?.data?.detail || "Sai định dạng file"), true);
    } finally {
      setLoading(false);
      e.target.value = null;
    }
  };

  // Mở modal và copy dữ liệu vào state form tạm
  const openEditModal = (item) => {
    setEditingItem(item);
    setEditFormData({ ...item });
    setIsEditModalOpen(true);
  };

  // Cập nhật state form khi người dùng gõ
  const handleFormChange = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Gọi API lưu dữ liệu
  const handleUpdateItem = async () => {
    setIsSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      // Giả định backend có endpoint PUT /api/categories/{type}/{id}
      await axios.put(`${API_URL}/categories/${activeTab}/${editFormData.id}`, editFormData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      showToast("Cập nhật thông tin thành công!");
      setIsEditModalOpen(false);
      
      // Cập nhật lại giao diện ngay lập tức mà không cần gọi lại API GET
      setData(prevData => prevData.map(item => 
        item.id === editFormData.id ? { ...editFormData } : item
      ));

    } catch (err) {
      console.error(err);
      showToast(err.response?.data?.detail || "Lỗi khi lưu dữ liệu!", true);
    } finally {
      setIsSaving(false);
    }
  };

  // Lấy danh sách cột động, loại bỏ các cột không cần thiết hiển thị/sửa
  const getHeaders = () => {
    if (data.length === 0) return [];
    return Object.keys(data[0]).filter(key => 
      !['id', 'submission_id', 'STT', 'facility_id', 'created_at', 'updated_at'].includes(key)
    );
  };

  const filteredData = data.filter(item => 
    Object.values(item).some(val => 
      String(val).toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage, 
    currentPage * itemsPerPage
  );

  return (
    <div className="flex flex-col h-full w-full gap-3 lg:gap-4 relative font-sans text-slate-600">
      
      {/* ================= TOAST THÔNG BÁO ================= */}
      {toastMessage && (
        <div className={`fixed bottom-24 lg:bottom-10 right-1/2 translate-x-1/2 lg:translate-x-0 lg:right-8 z-[200] text-white px-5 py-3.5 rounded-2xl shadow-2xl flex items-center gap-3 animate-slide-up-fade whitespace-nowrap ${toastMessage.isError ? 'bg-rose-600' : 'bg-slate-800'}`}>
          {toastMessage.isError ? <AlertCircle size={18} className="text-rose-200" /> : <CheckCircle2 size={18} className="text-emerald-400" />}
          <span className="text-sm font-bold">{toastMessage.text}</span>
        </div>
      )}

      {/* 1. TOOLBAR */}
      <div className="shrink-0 bg-white/70 backdrop-blur-xl border border-white rounded-2xl px-3 lg:px-5 py-3 flex flex-col lg:flex-row lg:items-center justify-between gap-3 shadow-sm z-20">
        
        {/* Nhóm Tabs */}
        <div className="flex gap-1.5 p-1 bg-white/60 border border-slate-200/50 rounded-xl w-full lg:w-fit overflow-x-auto flex-nowrap no-scrollbar snap-x shadow-inner">
          <Tab active={activeTab === 'medicine'} onClick={() => setActiveTab('medicine')} icon={<Activity size={14}/>} label="Thuốc" />
          <Tab active={activeTab === 'dvkt'} onClick={() => setActiveTab('dvkt')} icon={<Stethoscope size={14}/>} label="Dịch vụ" />
          <Tab active={activeTab === 'supply'} onClick={() => setActiveTab('supply')} icon={<Package size={14}/>} label="Vật tư" />
          <Tab active={activeTab === 'staff'} onClick={() => setActiveTab('staff')} icon={<Users size={14}/>} label="Nhân sự" />
          <Tab active={activeTab === 'hospital'} onClick={() => setActiveTab('hospital')} icon={<Building2 size={14}/>} label="Cơ sở KCB" />
        </div>

        {/* Nhóm Search & Import */}
        <div className="flex items-center gap-2 lg:gap-3 w-full lg:w-auto">
          <div className="relative group flex-1 lg:w-64">
            <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-sky-500 transition-colors" />
            <input 
              type="text" 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Tìm nhanh..." 
              className="w-full pl-9 pr-3 py-2 bg-white/80 border border-slate-200/60 rounded-xl text-xs outline-none focus:bg-white focus:border-sky-300 focus:ring-2 focus:ring-sky-50 transition-all font-medium shadow-sm"
            />
          </div>

          {hasRole('admin') && (
            <label className="flex items-center justify-center gap-2 bg-slate-800 text-white px-4 py-2 rounded-xl text-xs font-bold tracking-wide hover:bg-sky-600 transition-colors cursor-pointer shadow-sm shrink-0 active:scale-95 h-full">
              <Upload size={14} />
              <span className="hidden sm:inline">Import</span>
              <input type="file" hidden accept=".xlsx, .xls, .csv" onChange={handleImport} />
            </label>
          )}
        </div>
      </div>

      {/* 2. VÙNG DỮ LIỆU CHÍNH */}
      <main className="flex-1 bg-white/70 backdrop-blur-xl rounded-2xl border border-white shadow-sm flex flex-col overflow-hidden relative">
        
        <div className="flex-1 w-full overflow-auto custom-scrollbar">
          
          {/* DESKTOP VIEW */}
          <div className="hidden md:block min-w-max">
            <table className="w-full text-left border-collapse">
              <thead className="sticky top-0 z-10 bg-slate-50/90 backdrop-blur-md">
                <tr className="text-[10px] font-black text-slate-500 uppercase tracking-widest border-b border-slate-200/60">
                  <th className="px-5 py-4 w-10 text-center">#</th>
                  {getHeaders().map(header => (
                    <th key={header} className="px-5 py-4 whitespace-nowrap">{header.replace(/_/g, ' ')}</th>
                  ))}
                  <th className="px-5 py-4 text-center sticky right-0 bg-slate-50/90 backdrop-blur-md z-10 border-l border-slate-200/60 shadow-[-10px_0_15px_-10px_rgba(0,0,0,0.05)]">Sửa</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100/60">
                {loading ? (
                  <tr><td colSpan={getHeaders().length + 2} className="py-20 text-center text-slate-400 italic animate-pulse">Đang đồng bộ dữ liệu...</td></tr>
                ) : paginatedData.length === 0 ? (
                  <tr><td colSpan={getHeaders().length + 2} className="py-20 text-center text-slate-400 font-medium">Không tìm thấy dữ liệu phù hợp</td></tr>
                ) : paginatedData.map((item, index) => (
                  <tr key={item.id} className="group hover:bg-sky-50/50 transition-colors cursor-pointer" onDoubleClick={() => openEditModal(item)}>
                    <td className="px-5 py-3.5 text-xs font-mono text-slate-400 text-center">
                      {(currentPage - 1) * itemsPerPage + index + 1}
                    </td>
                    {getHeaders().map(header => (
                      <td key={header} className="px-5 py-3.5 text-xs font-semibold text-slate-600 max-w-[250px] truncate" title={item[header]}>
                        {item[header] !== null && item[header] !== "" ? String(item[header]) : <span className="text-slate-300">-</span>}
                      </td>
                    ))}
                    <td className="px-5 py-3.5 text-center sticky right-0 bg-white/50 backdrop-blur-sm group-hover:bg-sky-50/80 border-l border-slate-100/60 shadow-[-10px_0_15px_-10px_rgba(0,0,0,0.02)] transition-colors">
                      <button 
                        onClick={() => openEditModal(item)}
                        className="p-1.5 text-slate-400 hover:text-sky-600 hover:bg-white rounded-lg border border-transparent hover:border-sky-200 transition-all shadow-sm"
                      >
                        <Edit3 size={15} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* MOBILE VIEW (TỐI ƯU MỚI) */}
          <div className="md:hidden p-3 space-y-3 pb-10">
            {loading ? (
               <div className="py-10 text-center text-slate-400 italic animate-pulse text-sm">Đang đồng bộ...</div>
            ) : paginatedData.length === 0 ? (
               <div className="py-10 text-center text-slate-400 font-medium text-sm">Không tìm thấy dữ liệu</div>
            ) : paginatedData.map((item) => (
              <div 
                key={item.id} 
                onClick={() => openEditModal(item)}
                className="bg-white/80 backdrop-blur-sm p-4 rounded-2xl border border-slate-100 shadow-sm relative overflow-hidden group active:scale-[0.98] transition-transform cursor-pointer"
              >
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-sky-300"></div>
                <div className="flex flex-col gap-2 ml-1">
                  <div className="flex justify-between items-start">
                    <span className="text-[10px] font-mono font-bold text-sky-600 bg-sky-50 px-2 py-0.5 rounded border border-sky-100">
                      {item.Mã || item.MA_THUOC || item.MA_DVKT || item.MA_VAT_TU || item.MA_NHAN_VIEN || 'ID: ' + item.id}
                    </span>
                    <Edit3 size={14} className="text-slate-300" />
                  </div>
                  
                  <div>
                    <h4 className="font-bold text-slate-800 text-[13px] leading-snug">
                      {item.Tên || item.TEN_THUOC || item.TEN_DVKT_GIA || item.TEN_VAT_TU || item.HO_TEN || 'Chưa định danh'}
                    </h4>
                    {(item.Địa_chỉ || item.Tuyến_CMKT || item.MA_BHXH) && (
                      <p className="text-[11px] text-slate-500 line-clamp-1 italic mt-0.5">
                        {item.Địa_chỉ || `Tuyến: ${item.Tuyến_CMKT}` || `BHXH: ${item.MA_BHXH}`}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

        </div>

        {/* --- THANH PHÂN TRANG --- */}
        {!loading && filteredData.length > 0 && (
          <div className="bg-white/50 backdrop-blur-md border-t border-slate-100/60 px-4 py-2.5 lg:px-5 lg:py-3 shrink-0 flex items-center justify-between z-10">
            <div className="text-[10px] lg:text-[11px] font-semibold text-slate-500">
              <span className="hidden sm:inline">Hiển thị</span> <span className="text-slate-800">{(currentPage - 1) * itemsPerPage + 1}</span> - <span className="text-slate-800">{Math.min(currentPage * itemsPerPage, filteredData.length)}</span> / <span className="text-sky-600 font-bold">{filteredData.length}</span>
            </div>
            
            <div className="flex items-center gap-2">
              <button 
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="p-1.5 rounded-lg bg-white text-slate-500 hover:bg-sky-50 hover:text-sky-600 disabled:opacity-50 transition-colors border border-slate-200/60 shadow-sm"
              >
                <ChevronLeft size={16} />
              </button>
              
              <div className="px-3 py-1 rounded-lg bg-sky-50 text-sky-600 text-[11px] lg:text-xs font-bold font-mono border border-sky-100">
                {currentPage} / {totalPages}
              </div>

              <button 
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="p-1.5 rounded-lg bg-white text-slate-500 hover:bg-sky-50 hover:text-sky-600 disabled:opacity-50 transition-colors border border-slate-200/60 shadow-sm"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </main>

      {/* 3. MODAL (DRAWER) - DYNAMIC FORM */}
      {isEditModalOpen && editingItem && (
        <div className="fixed inset-0 z-[100] flex justify-end">
          <div className="absolute inset-0 bg-slate-900/20 backdrop-blur-sm animate-fade-in" onClick={() => setIsEditModalOpen(false)}></div>
          <div className="relative bg-white h-full w-full max-w-sm sm:max-w-md shadow-2xl flex flex-col animate-slide-in border-l border-slate-200">
            <div className="p-4 lg:p-5 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
              <h3 className="text-xs font-black text-slate-800 uppercase tracking-widest flex items-center gap-2">
                <Edit3 size={16} className="text-sky-500"/> Hiệu chỉnh thông tin
              </h3>
              <button onClick={() => setIsEditModalOpen(false)} className="p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-500 rounded-lg transition-colors"><X size={18} strokeWidth={2.5}/></button>
            </div>
            
            <div className="p-5 flex-1 overflow-y-auto space-y-4 custom-scrollbar pb-10">
               <section className="p-4 bg-sky-50/50 rounded-2xl border border-sky-100 mb-6">
                <span className="text-[9px] font-black text-sky-500 uppercase tracking-widest">Đang chỉnh sửa</span>
                <p className="text-sm font-bold text-slate-800 mt-1 uppercase leading-snug">
                  {editingItem.Tên || editingItem.TEN_THUOC || editingItem.TEN_DVKT_GIA || editingItem.TEN_VAT_TU || editingItem.HO_TEN || editingItem.Mã || 'Không rõ'}
                </p>
              </section>
              
              {/* RENDER FORM DYNAMIC TỪ GETHEADERS() */}
              <div className="space-y-4">
                {getHeaders().map((key) => (
                  <ThinInput 
                    key={key}
                    label={key.replace(/_/g, ' ')} 
                    value={editFormData[key]} 
                    onChange={(val) => handleFormChange(key, val)}
                  />
                ))}
              </div>
            </div>
            
            <div className="p-4 lg:p-5 border-t border-slate-100 bg-white pb-[calc(1rem+env(safe-area-inset-bottom))]">
              <button 
                onClick={handleUpdateItem}
                disabled={isSaving}
                className="w-full bg-sky-600 text-white py-3.5 rounded-xl font-bold text-xs hover:bg-sky-700 active:scale-[0.98] transition-all shadow-md shadow-sky-600/20 flex items-center justify-center gap-2 disabled:opacity-70"
              >
                {isSaving ? <RefreshCw size={16} className="animate-spin" /> : <Save size={16}/>} 
                {isSaving ? 'ĐANG LƯU...' : 'LƯU THAY ĐỔI'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- CSS cho Scrollbar và Animation --- */}
      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 5px; height: 5px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 8px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        
        @keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }
        .animate-slide-in { animation: slideInRight 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        .animate-fade-in { animation: fadeIn 0.3s ease-out forwards; }
        
        @keyframes slideUpFade {
          from { opacity: 0; transform: translateY(20px) translateX(-50%); }
          to { opacity: 1; transform: translateY(0) translateX(-50%); }
        }
        @media (min-width: 1024px) {
          @keyframes slideUpFade {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
        }
        .animate-slide-up-fade { animation: slideUpFade 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
      `}</style>
    </div>
  );
};

// Component Input có Two-way binding
const ThinInput = ({ label, value, onChange, type="text", placeholder }) => (
  <div className="space-y-1.5">
    <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">{label}</label>
    <input 
      type={type} 
      value={value !== null && value !== undefined ? value : ''} 
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full px-3.5 py-2.5 bg-slate-50/50 border border-slate-200/80 rounded-xl text-xs outline-none focus:bg-white focus:border-sky-300 focus:ring-2 focus:ring-sky-50 transition-all font-semibold text-slate-700"
    />
  </div>
);

const Tab = ({ active, onClick, icon, label }) => (
  <button 
    onClick={onClick}
    className={`flex items-center gap-2 px-4 lg:px-5 py-2 rounded-lg text-[10px] lg:text-[11px] font-bold transition-all shrink-0 snap-start ${
      active 
        ? 'bg-white text-sky-600 shadow-sm border border-slate-100' 
        : 'text-slate-400 hover:text-slate-600 hover:bg-slate-50/50 border border-transparent'
    }`}
  >
    {icon} <span className="uppercase tracking-tighter">{label}</span>
  </button>
);

export default MasterDataManagement;